import React from 'react';

const STYLE_ID = 'app-route-fallback-style';

const injectStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;

  const tag = document.createElement('style');
  tag.id = STYLE_ID;
  tag.textContent = `
    @keyframes app-route-fallback-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .app-route-fallback {
      width: 100%;
      padding: 24px 15px;
    }
    .app-route-fallback__inner {
      width: 100%;
      max-width: 1200px;
      margin: 0 auto;
    }
    .app-route-fallback__surface {
      background: #fff;
      border: 1px solid #eef2f7;
      border-radius: 6px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      padding: 20px;
    }
    .app-route-fallback__bar,
    .app-route-fallback__line,
    .app-route-fallback__cell,
    .app-route-fallback__block,
    .app-route-fallback__circle {
      background: linear-gradient(90deg, #edf2f7 25%, #f8fafc 50%, #edf2f7 75%);
      background-size: 200% 100%;
      animation: app-route-fallback-shimmer 1.35s ease-in-out infinite;
    }
    .app-route-fallback__bar {
      height: 18px;
      border-radius: 4px;
      margin-bottom: 18px;
    }
    .app-route-fallback__bar--short { width: 28%; max-width: 220px; }
    .app-route-fallback__bar--medium { width: 44%; max-width: 380px; }
    .app-route-fallback__line {
      height: 14px;
      border-radius: 4px;
      margin-bottom: 12px;
    }
    .app-route-fallback__line--wide { width: 88%; }
    .app-route-fallback__line--medium { width: 64%; }
    .app-route-fallback__line--short { width: 38%; }
    .app-route-fallback__toolbar {
      display: grid;
      grid-template-columns: minmax(140px, 1fr) minmax(100px, 160px) minmax(100px, 160px);
      gap: 12px;
      margin-bottom: 18px;
    }
    .app-route-fallback__table {
      display: grid;
      gap: 10px;
    }
    .app-route-fallback__row {
      display: grid;
      grid-template-columns: 1.4fr 1fr 1fr 0.8fr;
      gap: 12px;
      align-items: center;
    }
    .app-route-fallback__cell {
      height: 16px;
      border-radius: 4px;
    }
    .app-route-fallback__detail {
      display: grid;
      grid-template-columns: 96px 1fr;
      gap: 18px;
      align-items: start;
    }
    .app-route-fallback__circle {
      width: 72px;
      height: 72px;
      border-radius: 50%;
    }
    .app-route-fallback__form {
      display: grid;
      gap: 16px;
    }
    .app-route-fallback__field {
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 16px;
      align-items: center;
    }
    .app-route-fallback__block {
      height: 38px;
      border-radius: 5px;
    }
    @media (max-width: 767px) {
      .app-route-fallback { padding: 16px 12px; }
      .app-route-fallback__surface { padding: 16px; }
      .app-route-fallback__toolbar,
      .app-route-fallback__row,
      .app-route-fallback__detail,
      .app-route-fallback__field {
        grid-template-columns: 1fr;
      }
      .app-route-fallback__bar--short,
      .app-route-fallback__bar--medium,
      .app-route-fallback__line--wide,
      .app-route-fallback__line--medium,
      .app-route-fallback__line--short {
        width: 100%;
      }
    }
  `;
  document.head.appendChild(tag);
};

const SkeletonLine = ({ size = 'wide' }) => <div className={`app-route-fallback__line app-route-fallback__line--${size}`} />;

const TableFallback = ({ rows }) => (
  <div className="app-route-fallback__surface">
    <div className="app-route-fallback__bar app-route-fallback__bar--medium" />
    <div className="app-route-fallback__toolbar">
      <div className="app-route-fallback__block" />
      <div className="app-route-fallback__block" />
      <div className="app-route-fallback__block" />
    </div>
    <div className="app-route-fallback__table">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div className="app-route-fallback__row" key={rowIndex}>
          <div className="app-route-fallback__cell" />
          <div className="app-route-fallback__cell" />
          <div className="app-route-fallback__cell" />
          <div className="app-route-fallback__cell" />
        </div>
      ))}
    </div>
  </div>
);

const DetailFallback = () => (
  <div className="app-route-fallback__surface app-route-fallback__detail">
    <div className="app-route-fallback__circle" />
    <div>
      <div className="app-route-fallback__bar app-route-fallback__bar--medium" />
      <SkeletonLine size="wide" />
      <SkeletonLine size="medium" />
      <SkeletonLine size="short" />
    </div>
  </div>
);

const FormFallback = ({ rows }) => (
  <div className="app-route-fallback__surface">
    <div className="app-route-fallback__bar app-route-fallback__bar--short" />
    <div className="app-route-fallback__form">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div className="app-route-fallback__field" key={rowIndex}>
          <div className="app-route-fallback__line app-route-fallback__line--medium" />
          <div className="app-route-fallback__block" />
        </div>
      ))}
    </div>
  </div>
);

const PageFallback = () => (
  <div className="app-route-fallback__surface">
    <div className="app-route-fallback__bar app-route-fallback__bar--medium" />
    <SkeletonLine size="wide" />
    <SkeletonLine size="medium" />
    <SkeletonLine size="wide" />
    <SkeletonLine size="short" />
  </div>
);

const RouteFallback = ({ variant = 'table', rows = 8 }) => {
  React.useEffect(() => {
    injectStyles();
  }, []);

  const content = variant === 'detail'
    ? <DetailFallback />
    : variant === 'form'
      ? <FormFallback rows={rows} />
      : variant === 'page'
        ? <PageFallback />
        : <TableFallback rows={rows} />;

  return (
    <div className="app-route-fallback" role="status" aria-live="polite" aria-busy="true">
      <div className="app-route-fallback__inner">{content}</div>
    </div>
  );
};

export default RouteFallback;